import { useAuth, useSignIn } from "@clerk/react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { AuthScreen } from "@/components/auth/auth-screen";
import { CodeInput } from "@/components/auth/code-input";
import { PrimaryButton } from "@/components/auth/primary-button";
import { ResendRow } from "@/components/auth/resend-row";
import { TextLink } from "@/components/auth/text-link";
import { authPath } from "@/lib/auth-helpers";
import { clerkFailureMessage } from "@/lib/clerk-errors";
import { codeDigits, emptyCode } from "@/lib/code-input";
import { clearCodeSent } from "@/lib/code-sent";
import { finishSignIn } from "@/lib/finish-sign-in";
import { useSendCodeOnce } from "@/lib/use-send-code-once";

const FLOW = "device-trust";

const DeviceTrustPage = () => {
  const navigate = useNavigate();
  const { isLoaded } = useAuth();
  const { fetchStatus, signIn } = useSignIn();
  const [code, setCode] = useState(emptyCode);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const email = signIn.identifier ?? "your email";
  const ready = signIn.status === "needs_client_trust";

  useEffect(() => {
    if (isLoaded && !ready) {
      navigate(authPath("/login"), { replace: true });
    }
  }, [isLoaded, navigate, ready]);

  const send = useCallback(() => signIn.mfa.sendEmailCode(), [signIn]);
  useSendCodeOnce(FLOW, ready, send);

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const digits = codeDigits(code);
    if (digits.length !== 6) {
      setError("Enter the 6-digit code from your email.");
      return;
    }
    setError(null);
    setPending(true);
    const { error: verifyError } = await signIn.mfa.verifyEmailCode({
      code: digits,
    });
    if (verifyError) {
      setError(clerkFailureMessage(verifyError));
      setPending(false);
      return;
    }
    const message = await finishSignIn(signIn, navigate);
    setError(message);
    setPending(false);
  };

  return (
    <AuthScreen
      subtitle={`We sent a 6-digit code to ${email} to verify this device.`}
      title="Confirm it’s you"
    >
      <form className="flex flex-col gap-8" onSubmit={onSubmit}>
        <CodeInput
          error={error}
          id="device-code"
          label="Verification code"
          onChange={(value) => {
            setCode(value);
            setError(null);
          }}
          value={code}
        />
        <PrimaryButton
          busy={pending || fetchStatus === "fetching"}
          type="submit"
        >
          Verify
        </PrimaryButton>
        <ResendRow
          alternate={
            <TextLink to={authPath("/login")}>Back to sign in</TextLink>
          }
          flow={FLOW}
          onResend={async () => {
            const { error: resendError } = await signIn.mfa.sendEmailCode();
            if (resendError) {
              clearCodeSent(FLOW);
              setError(clerkFailureMessage(resendError));
              return;
            }
            setCode(emptyCode());
            setError(null);
          }}
        />
      </form>
    </AuthScreen>
  );
};

export default DeviceTrustPage;
